import { t as tr } from '../i18n.js'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router'
import { Diamond } from 'lucide-react'
import { api, type PortfolioOverview } from '../api.js'
import { fmtMinutes } from '../time.js'
import { Stat } from './charts.js'
import { Progress } from './Home.js'
import { projectColor } from './projectColors.js'
import './portfolio.css'

/** Colour swatch for a project row; falls back to the palette slot by portfolio order (same rule as the merged views). */
export function Swatch({ color, i }: { color: string | null; i: number }) {
  return <span className="swatch" style={{ '--pc': color ?? projectColor(i) } as React.CSSProperties} aria-hidden />
}

export function PortfolioOverviewPanel({ portfolioId }: { portfolioId: string }) {
  const q = useQuery({ queryKey: ['portfolio-overview', portfolioId], queryFn: () => api<PortfolioOverview>(`/portfolios/${portfolioId}/overview`) })
  if (q.isError) return <p className="bad">{tr("Could not load the overview.")}</p>
  if (!q.data) return <p className="muted">{tr("Loading…")}</p>
  const d = q.data
  const t = d.totals.tasks, est = d.totals.hours.estimated, logged = d.totals.hours.logged / 60
  const pct = t.total ? Math.round((t.done / t.total) * 100) : 0
  const canSeeAll = d.projects.every((p) => p.canSeeAllTime)
  return (
    <>
      <div className="stats">
        <Stat n={`${pct}%`} label={tr("tasks done")} sub={`${t.done} of ${t.total} · ${t.inProgress} in progress`} />
        <Stat n={t.overdue} label={tr("overdue")} sub={`${t.dueSoon} due in 7 days · ${t.unassigned} unassigned`} bad={t.overdue > 0} />
        <Stat n={fmtMinutes(d.totals.hours.logged)} label={canSeeAll ? tr("hours logged") : tr("hours logged (yours where restricted)")} sub={est ? `of ${est}h estimated (${Math.round((logged / est) * 100)}%)` : tr("no estimates yet")} bad={est > 0 && logged > est} />
        <Stat n={d.portfolioEnd ?? '—'} label={tr("portfolio end")} sub={d.portfolioEnd ? tr("latest scheduled finish across projects") : tr("no scheduled tasks yet")} />
        <Stat n={d.criticalCount} label={tr("critical tasks")} sub={d.criticalCount ? tr("zero slack: a delay moves the end date") : undefined} bad={d.criticalCount > 0} />
      </div>
      <Progress done={t.done} total={t.total} />

      <section className="card stack">
        <h2>{tr("Projects")}</h2>
        {d.projects.length === 0 && <p className="muted small">{tr("No projects in this portfolio yet.")}</p>}
        {d.projects.length > 0 && (
          <table className="table pftable">
            <thead><tr><th>{tr("Project")}</th><th>{tr("Progress")}</th><th className="num">{tr("Overdue")}</th><th>{tr("Next milestone")}</th><th>{tr("Schedule end")}</th><th>{tr("Auto-schedule")}</th></tr></thead>
            <tbody>
              {d.projects.map((p, i) => (
                <tr key={p.id} className={p.status === 'archived' ? 'muted' : ''}>
                  <td className="code"><Swatch color={p.color} i={i} /> <Link to={`/projects/${p.id}`}><strong>{p.code}</strong></Link> <span className="muted">{p.name}</span></td>
                  <td className="prog"><Progress done={p.tasks.done} total={p.tasks.total} /></td>
                  <td className={`num mono ${p.tasks.overdue ? 'bad' : ''}`}>{p.tasks.overdue || ''}</td>
                  <td className="small">{p.milestones.next
                    ? <><Diamond size={12} aria-hidden /> {p.milestones.next.name} <span className="muted">{p.milestones.next.dueOn}</span></>
                    : <span className="muted">{p.milestones.total ? `${p.milestones.done}/${p.milestones.total} ${tr("done")}` : '—'}</span>}
                    {p.milestones.overdue > 0 && <span className="tag bad">{p.milestones.overdue} {tr("overdue")}</span>}</td>
                  <td className="small mono">{p.scheduleEnd ?? <span className="muted">—</span>}</td>
                  <td className="small">{p.autoSchedule ? tr("on") : <span className="muted">{tr("off")}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {d.hiddenProjects > 0 && <p className="muted small">{d.hiddenProjects === 1 ? tr("1 private project is hidden from these totals.") : tr("{n} private projects are hidden from these totals.", { n: d.hiddenProjects })}</p>}
      </section>

      <section className="card stack">
        <h2>{tr("Upcoming milestones")}</h2>
        {d.upcomingMilestones.length === 0 && <p className="muted small">{tr("No open milestones with a due date.")}</p>}
        <div className="upcoming">
          {d.upcomingMilestones.map((m) => {
            const i = d.projects.findIndex((p) => p.id === m.projectId)
            const p = d.projects[i]
            return (
              <div key={m.id} className="row between small">
                <span><Swatch color={p?.color ?? null} i={i < 0 ? 0 : i} /> <Link to={`/projects/${m.projectId}`}><strong>{m.projectCode}</strong></Link> {m.name}</span>
                <span className={`when ${m.overdue ? 'bad' : 'muted'}`}>{m.dueOn}{m.overdue ? ` · ${tr("overdue")}` : ''}</span>
              </div>
            )
          })}
        </div>
      </section>
    </>
  )
}
